import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingCart, Search, ChevronDown, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Logo from "@/components/Logo";
import { useCart } from "@/contexts/CartContext";
import { CartDrawer } from "@/components/cart/CartDrawer";
import { useCategories } from "@/hooks/useCategories";
import { cn } from "@/lib/utils";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const { itemCount, setIsOpen } = useCart();
  const { data: categories = [] } = useCategories();
  const navigate = useNavigate();
  const categoryMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryMenuRef.current && !categoryMenuRef.current.contains(event.target as Node)) {
        setCategoryMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/catalogo?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
    }
  };

  const navLinks = [
    { name: "Inicio", href: "/" },
    { name: "Catálogo", href: "/catalogo" },
    { name: "Nosotros", href: "/nosotros" },
    { name: "Contacto", href: "/contacto" },
  ];

  return (
    <>
      {/* Top bar */}
      <div className="bg-primary text-primary-foreground text-sm py-2 hidden md:block">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="tel:+50600000000" className="flex items-center gap-2 hover:text-white/80 transition-colors">
              <Phone className="h-4 w-4" />
              <span>+506 0000 0000</span>
            </a>
            <span className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span>San José, Costa Rica</span>
            </span>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header
        className={cn(
          "sticky top-0 z-50 w-full bg-background border-b border-border transition-shadow duration-300",
          scrolled && "shadow-card",
        )}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <Logo className="h-8 w-auto" />
              <div className="hidden sm:flex flex-col">
                <span className="text-lg font-bold text-primary leading-tight">Tekton</span>
                <span className="text-[10px] text-muted-foreground leading-none">Toneladas de tecnología</span>
              </div>
            </Link>

            {/* Search bar - Desktop */}
            <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-xl mx-4">
              <div className="relative w-full">
                <Input
                  type="search"
                  placeholder="Buscar productos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-10 pl-4 pr-12 rounded-md border-2 border-primary/20 focus:border-primary"
                />
                <button
                  type="submit"
                  className="absolute right-0 top-0 h-10 w-10 flex items-center justify-center bg-primary text-white rounded-r-md hover:bg-primary/90 transition-colors"
                >
                  <Search className="h-5 w-5" />
                </button>
              </div>
            </form>

            {/* Right section */}
            <div className="flex items-center gap-2">
              {/* Cart Button */}
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => setIsOpen(true)}
                aria-label="Abrir carrito"
              >
                <ShoppingCart className="h-5 w-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-cta text-white text-xs font-bold flex items-center justify-center animate-badge-pulse">
                    {itemCount > 99 ? "99+" : itemCount}
                  </span>
                )}
              </Button>

              {/* Mobile menu button */}
              <button
                className="md:hidden p-2 rounded-md hover:bg-muted transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6 text-foreground" />
                ) : (
                  <Menu className="h-6 w-6 text-foreground" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Category navigation bar */}
        <div className="hidden md:block bg-muted border-t border-border">
          <div className="container mx-auto px-4">
            <div className="flex items-center h-12">
              {/* Category dropdown */}
              <div className="relative" ref={categoryMenuRef}>
                <button
                  onClick={() => setCategoryMenuOpen(!categoryMenuOpen)}
                  className={cn(
                    "flex items-center gap-2 px-4 h-12 font-medium transition-colors",
                    "hover:bg-primary hover:text-white",
                    categoryMenuOpen && "bg-primary text-white",
                  )}
                >
                  <Menu className="h-5 w-5" />
                  <span>Categorías</span>
                  <ChevronDown className={cn("h-4 w-4 transition-transform", categoryMenuOpen && "rotate-180")} />
                </button>

                {categoryMenuOpen && (
                  <div className="absolute top-full left-0 w-64 bg-card border border-border shadow-elevated z-50 rounded-b-md">
                    {categories.map((category) => (
                      <Link
                        key={category.id}
                        to={`/catalogo?category=${category.slug}`}
                        className="block px-4 py-3 hover:bg-muted transition-colors border-b border-border last:border-b-0"
                        onClick={() => setCategoryMenuOpen(false)}
                      >
                        {category.name}
                      </Link>
                    ))}
                    <Link
                      to="/catalogo"
                      className="block px-4 py-3 text-primary font-medium hover:bg-muted transition-colors"
                      onClick={() => setCategoryMenuOpen(false)}
                    >
                      Ver todo el catálogo →
                    </Link>
                  </div>
                )}
              </div>

              {/* Nav links */}
              <nav className="flex items-center ml-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="px-4 h-12 flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.name}
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div
          className={cn(
            "md:hidden overflow-hidden transition-all duration-300 bg-background border-t border-border",
            mobileMenuOpen ? "max-h-[500px]" : "max-h-0",
          )}
        >
          {/* Mobile search */}
          <form onSubmit={handleSearch} className="p-4 border-b border-border">
            <div className="relative">
              <Input
                type="search"
                placeholder="Buscar productos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-4 pr-12"
              />
              <button
                type="submit"
                className="absolute right-0 top-0 h-10 w-10 flex items-center justify-center bg-primary text-white rounded-r-md"
              >
                <Search className="h-5 w-5" />
              </button>
            </div>
          </form>

          {/* Mobile nav links */}
          <nav className="p-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="block py-3 px-4 text-base font-medium rounded-md hover:bg-muted transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Mobile categories */}
          <div className="p-4 border-t border-border">
            <p className="text-sm font-medium text-muted-foreground mb-2">Categorías</p>
            <div className="space-y-1">
              {categories.slice(0, 5).map((category) => (
                <Link
                  key={category.id}
                  to={`/catalogo?category=${category.slug}`}
                  className="block py-2 px-4 text-sm rounded-md hover:bg-muted transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </header>

      <CartDrawer />
    </>
  );
};

export default Header;
